import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchSalesOrderDetail } from '#/api/crmMgmt/salesOrder/fetchDetail';
import { fetchSalesOrderSnCandidates } from '#/api/crmMgmt/salesOrder/fetchSnCandidates';
import { updateSalesOrderSnSelections } from '#/api/crmMgmt/salesOrder/updateSnSelections';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import SalesOrderEditReferenceSections from '#/modules/crmMgmt/salesOrder/components/SalesOrderEditReferenceSections';
import SalesOrderSnCandidateChecklist from '#/modules/crmMgmt/salesOrder/components/SalesOrderSnCandidateChecklist';
import SalesOrderSnLineMtrTabs from '#/modules/crmMgmt/salesOrder/components/SalesOrderSnLineMtrTabs';
import SalesOrderStatusBadge from '#/modules/crmMgmt/salesOrder/components/SalesOrderStatusBadge';
import {
  getSalesOrderApiErrorMessage,
  isSalesOrderVersionConflict,
} from '#/modules/crmMgmt/salesOrder/helper/apiError';
import {
  buildSalesOrderSnSelectionMap,
  buildSalesOrderSnSelectionsPayload,
  isSalesOrderSnMtrComplete,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderSnSelection';
import { canEditSalesOrderDraft } from '#/modules/crmMgmt/salesOrder/helper/salesOrderStatus';
import CreateEditReferenceRail from '#/modules/infra/layout/CreateEditReferenceRail';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  salesOrderDetailQueryKey,
  salesOrderListQueryKeyPrefix,
  salesOrderSnCandidatesQueryKey,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

/**
 * 銷貨單「選擇序號」頁（僅 DRAFT）：產品 → 物料 → 逐件勾選 SN，PUT sn-selections 完整替換。
 */
const SalesOrderSnSelectPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /** 使用者勾選；綁定當次 candidates 回應，重新載入後回到後端已存選擇。 */
  const [draft, setDraft] = useState({ source: null, map: {} });

  const detailQuery = useQuery({
    queryKey: salesOrderDetailQueryKey(id),
    queryFn: () => fetchSalesOrderDetail(id),
    enabled: Boolean(id),
  });

  const detail = detailQuery.data?.data;
  const isDraft = canEditSalesOrderDraft(detail?.status);

  const candidatesQuery = useQuery({
    queryKey: salesOrderSnCandidatesQueryKey(id),
    queryFn: () => fetchSalesOrderSnCandidates(id),
    enabled: Boolean(id) && isDraft,
  });

  const snLines = useMemo(
    () => candidatesQuery.data?.data?.lines ?? [],
    [candidatesQuery.data],
  );
  const savedSelectionMap = useMemo(() => buildSalesOrderSnSelectionMap(snLines), [snLines]);
  const selectionMap = draft.source === candidatesQuery.data ? draft.map : savedSelectionMap;
  const allMtrs = snLines.flatMap((line) => line?.mtrs ?? []);
  const allComplete = allMtrs.length > 0 && allMtrs.every((mtr) => isSalesOrderSnMtrComplete(
    mtr,
    selectionMap[String(mtr.salesOrderLineMtrId)],
  ));

  useEffect(() => {
    if (detail && !canEditSalesOrderDraft(detail.status)) {
      toast.warning('僅草稿可選擇序號，已返回詳情');
      navigate(ROUTES.salesOrderDetail(id), { replace: true });
    }
  }, [detail, id, navigate]);

  const saveMutation = useMutation({
    retry: false,
    mutationFn: () => updateSalesOrderSnSelections(
      id,
      buildSalesOrderSnSelectionsPayload(snLines, selectionMap, {
        updatedAt: detail?.record?.updatedAt ?? detail?.updatedAt,
      }),
    ),
    onSuccess: async (result) => {
      toast.success(result?.message || '序號已儲存');
      await queryClient.invalidateQueries({ queryKey: salesOrderListQueryKeyPrefix() });
      await queryClient.invalidateQueries({ queryKey: salesOrderDetailQueryKey(id) });
      await queryClient.invalidateQueries({ queryKey: salesOrderSnCandidatesQueryKey(id) });
      navigate(ROUTES.salesOrderDetail(id));
    },
    onError: (error) => {
      if (isSalesOrderVersionConflict(error)) {
        toast.error(getSalesOrderApiErrorMessage(error, '版本衝突，請重新載入'));
        detailQuery.refetch();
        candidatesQuery.refetch();
        return;
      }
      toast.error(getSalesOrderApiErrorMessage(error, '儲存序號失敗'));
    },
  });

  function handleToggle(lineMtrId, snId, checked) {
    const current = selectionMap[lineMtrId] ?? [];
    const next = checked ? [...current, snId] : current.filter((item) => item !== snId);
    setDraft({
      source: candidatesQuery.data,
      map: { ...selectionMap, [lineMtrId]: next },
    });
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!allComplete) {
      toast.error('每個物料須選滿需求數量');
      return;
    }
    saveMutation.mutate();
  }

  const isPageLoading = Boolean(id) && detailQuery.isLoading;
  const canShowEditor = Boolean(detail) && isDraft;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="flex min-h-0 w-full flex-1 flex-col">
          <div className="create_edit_page__header">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
                <span className="border-l-5 border-primary-400 pl-2.5">選擇序號</span>
                {detail?.status ? (
                  <SalesOrderStatusBadge status={detail.status} size="sm" />
                ) : null}
              </h1>
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.salesOrderDetail(id)}>返回詳情</Link>
              </Button>
            </div>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {detailQuery.isError ? (
            <p className="text-sm text-destructive">
              {getSalesOrderApiErrorMessage(detailQuery.error, '找不到銷貨單')}
            </p>
          ) : null}

          {canShowEditor ? (
            <CreateEditReferenceRail
              className="min-h-0 flex-1"
              mainClassName="mx-auto w-full max-w-6xl lg:mr-auto"
              main={(
                <form
                  className="sheet create_edit create_edit_form create_edit_form--fill"
                  noValidate
                  onSubmit={handleSubmit}
                >
                  <div className="create_edit_form__body space-y-3 px-4 py-4">
                    <p className="text-xs text-muted-foreground">
                      依產品 → 物料逐件勾選序號；每個物料須選滿需求數量。儲存不占用，送審時才占用。
                    </p>
                    {candidatesQuery.isLoading ? (
                      <p className="text-sm text-slate-500">載入序號候選…</p>
                    ) : null}
                    {candidatesQuery.isError ? (
                      <p className="text-sm text-destructive">
                        {getSalesOrderApiErrorMessage(candidatesQuery.error, '載入序號候選失敗')}
                      </p>
                    ) : null}
                    {candidatesQuery.data ? (
                      <SalesOrderSnLineMtrTabs
                        detailLines={detail?.lines ?? []}
                        snLines={snLines}
                        selectionMap={selectionMap}
                        renderMtrContent={(mtr, selectedIds) => (
                          <SalesOrderSnCandidateChecklist
                            mtr={mtr}
                            selectedIds={selectedIds}
                            disabled={saveMutation.isPending}
                            onToggle={(snId, checked) => handleToggle(
                              String(mtr.salesOrderLineMtrId),
                              snId,
                              checked,
                            )}
                          />
                        )}
                      />
                    ) : null}
                  </div>

                  <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                    <Button type="button" variant="outline" asChild>
                      <Link to={ROUTES.salesOrderDetail(id)}>取消</Link>
                    </Button>
                    <Button type="submit" disabled={!allComplete || saveMutation.isPending}>
                      {saveMutation.isPending ? '儲存中…' : '儲存'}
                    </Button>
                  </div>
                </form>
              )}
              reference={<SalesOrderEditReferenceSections detail={detail} />}
            />
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default SalesOrderSnSelectPage;
